import { Button } from '@rcg/ui-components';
import AffirmBanner from 'components/AffirmBanner';
import AviosBanner from 'components/AviosBanner';
import EmbeddedSurvey from 'components/EmbeddedSurvey';
import { DEFAULT } from 'components/Scroller/constants';
import { withForceUpdateResize, withScrollingElement } from 'components/Scroller/context';
import { AnalyticsContext } from 'contexts/AnalyticsContext';
import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { getPorts } from 'contexts/CruiseSearchContext/utils/formatPorts';
import { CruiseSelectionContext } from 'contexts/CruiseSelectionContext';
import { CLOSED, SELECTING } from 'contexts/CruiseSelectionContext/constants';
import { PersonalizationContext } from 'contexts/PersonalizationContext';
import gsap, { Cubic } from 'gsap';
import { useConfig } from 'hooks';
import { SERVER_COMMON_TOGGLES } from 'lib/constants';
import { apolloClient } from 'lib/graphql/apolloClient';
import { getCruiseById } from 'lib/graphql/cruises/cruises.client';
import { getBrandUISettings } from 'lib/utilities/Brands';
import { withRouter } from 'next/router';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useFeatureFlags } from '../../hooks/LaunchDarkly/useFeatureFlags';
import { CruiseCard, CruiseCardVariant } from '../CruiseCard';
import { FILTERS_WRAPPER } from '../Filters/constants';
import { PROMO_WRAPPER, SEARCH_BAR } from '../SearchPage/Constants';
import { SELECTED_FILTERS_WRAPPER } from '../SelectedFilters/constants';
import NoResults from './NoResults';
import ResultsHeader from './ResultsHeader';
import ResultsLoading from './ResultsLoading';
import { BANNER_PLACEHOLDER_ID, LOAD_MORE_BUTTON_ID, PAGE_HEADER_ID, SCROLLING_OFFSET } from './constants';
import { BannerPlaceholder, BannerWrapper, ButtonContainer, Constrain, HeaderConstrain, ResultsWrapper } from './styles';

/**
 * Main section of the SearchPage
 * displaying SearchCard/search results.
 * Also includes the header combinations for search and compare
 * as well as the dropdown filters for sort and cabinClass
 *
 * @param {object} passedRefs - Used for animations
 * @param {object} scrollingElement - DOM element of parent scroller
 */

function Results({ scrollingElement, passedRefs, forceResize, router: { query } }) {
	const config = useConfig();
	const { search, qualifiers } = query;
	const { cruisesComparing, comparisonViewState, openCruiseDetail, setComparisonViewSelecting, setComparisonViewClosed } =
		useContext(CruiseSelectionContext);
	const {
		results,
		fetchMoreSearchResults,
		loading,
		locale,
		selectGroupIdDetail,
		portCodes,
		searchApplied,
		qualifiersApplied,
		openModalByUrl,
		getModalByUrl,
		filterOpen,
		globalization,
		brand,
		itineraryPanel,
		ssrCommonFlags,
	} = useContext(CruiseSearchContext);
	const { enableMedalliaEmbeddedSurvey, enableEventTrackng, enableAffirm, enableBannerPlaceholder, enableRiverCruises } = useFeatureFlags();

	const { generalDictionary } = globalization?.dictionaries || {};
	const { actionLoadMore } = generalDictionary;
	const { sendOpenItineraryPanelEvent } = useContext(PersonalizationContext);
	const { utagPagination } = useContext(AnalyticsContext);

	const wrapperRef = passedRefs?.current?.resultsRef;
	const headerRef = useRef();
	const [selectedCruise, setSelectedCruise] = useState(null);
	const [finishedAnimation, setFinishedAnimation] = useState(false);
	const [headerIsSticky, setHeaderIsSticky] = useState(false);
	const { uiSettings } = getBrandUISettings(brand);

	const selectingForCompare = comparisonViewState === SELECTING;

	const promoBannerEl = document.getElementById(PROMO_WRAPPER);
	const searchBarEl = document.getElementById(SEARCH_BAR);
	const filtersEl = document.getElementById(FILTERS_WRAPPER);
	const selectedFiltersEl = document.getElementById(SELECTED_FILTERS_WRAPPER);

	const scrollTopOffset = useMemo(
		() =>
			promoBannerEl?.offsetHeight +
			(searchBarEl?.offsetHeight ?? 0) +
			(filtersEl?.offsetHeight ?? 0) +
			(selectedFiltersEl?.offsetHeight ?? 0) -
			SCROLLING_OFFSET,
		[promoBannerEl, searchBarEl, filtersEl, selectedFiltersEl],
	);

	useEffect(() => {
		/**
		 * Need to offset header/main menu height
		 * so this will come from real environment
		 */
		const headerEl = document.getElementById('rciHeader');
		const { height: headerHeight = 70 } = headerEl?.getBoundingClientRect() || {};

		const onComplete = () => {
			forceResize();
			setFinishedAnimation(true);
			// sets correct position to any results scrolled past
			document.querySelectorAll('.results-card-item')?.forEach((item) => {
				if (item.getBoundingClientRect().y <= 0) {
					gsap.to(item, {
						y: 0,
						opacity: 1,
					});
				}
			});
		};
		const { top } = wrapperRef.current.getBoundingClientRect();
		const scrollTop = scrollingElement?.scrollTop || 0;
		const y = (top + scrollTop - headerHeight) * -1;
		const timelineCompare = gsap.timeline({ onComplete });
		const duration = 0.6;
		const ease = Cubic.easeInOut;

		if (selectingForCompare) {
			scrollingElement.scrollTop -= scrollTopOffset;
			timelineCompare.set(wrapperRef.current, { marginBottom: y, zIndex: 2 }).to(wrapperRef.current, { y, duration, ease }, 'together');
		} else {
			scrollingElement.scrollTop += scrollTopOffset;
			timelineCompare
				.to(wrapperRef.current, { y: 0, duration, ease }, 'together')
				.set(wrapperRef.current, { marginBottom: 0 })
				.set(wrapperRef.current, { zIndex: 'unset' });
		}
	}, [selectingForCompare]);

	const cruises = useMemo(() => results?.cruises || [], [results]);
	const count = results?.total;
	const more = count > cruises?.length;

	const getBannerToShow = useCallback(() => {
		if (ssrCommonFlags[SERVER_COMMON_TOGGLES.ENABLE_AVIOS]) return <AviosBanner />;
		if (enableBannerPlaceholder) {
			return (
				<BannerWrapper>
					<BannerPlaceholder id={BANNER_PLACEHOLDER_ID} />
				</BannerWrapper>
			);
		}
		if (enableAffirm) return <AffirmBanner />;

		return <></>;
	}, [enableBannerPlaceholder, enableAffirm]);

	const openCruise = useCallback(
		(cruise, index) => {
			const cruiseData = {
				...cruise,
				locale,
				searchIndex: index + 1,
				resultsLength: count,
				itineraries: cruises.map(({ id }) => id),
				cruiseRecommendationId: results?.cruiseRecommendationId,
			};
			openCruiseDetail(cruiseData);
			if (enableEventTrackng && cruise?.id) {
				sendOpenItineraryPanelEvent(
					{
						...cruiseData,
						searchApplied,
						qualifiersApplied,
						brand,
					},
					query,
					config,
				);
			}
		},
		[
			count,
			cruises,
			enableEventTrackng,
			locale,
			openCruiseDetail,
			results?.cruiseRecommendationId,
			sendOpenItineraryPanelEvent,
			searchApplied,
			qualifiersApplied,
			query,
			config,
		],
	);

	const fetchSingleCruiseById = useCallback(
		async (id, qualifiers) => {
			const { data, loading } = await getCruiseById(apolloClient, {
				id,
				filters: search,
				qualifiers,
				enableRiverCruises,
			});
			if (data?.cruise === null) {
				selectGroupIdDetail('');
			} else if (!loading) {
				const ports = getPorts(data?.cruise?.masterSailing?.itinerary?.days, portCodes);

				setSelectedCruise({
					...data?.cruise,
					masterSailing: {
						...data?.cruise?.masterSailing,
						itinerary: {
							...data?.cruise?.masterSailing.itinerary,
							ports,
						},
					},
					featuredImage: {
						path: data?.cruise?.masterSailing?.itinerary.media?.images[0]?.path,
					},
					sailings: data?.cruise?.sailings.sort((c1, c2) => (c1.sailDate > c2.sailDate ? 1 : -1)),
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[portCodes, selectGroupIdDetail, enableRiverCruises],
	);

	useEffect(() => {
		const popupModal = getModalByUrl();
		if (itineraryPanel && !popupModal) {
			const selectedCruise = cruises.find((cruise) => cruise.id === itineraryPanel);
			if (selectedCruise) {
				setSelectedCruise(selectedCruise);
			} else {
				fetchSingleCruiseById(itineraryPanel, qualifiers);
			}
		}
	}, [cruises, itineraryPanel, qualifiers, fetchSingleCruiseById, getModalByUrl]);

	useEffect(() => {
		if (finishedAnimation) {
			const popupModal = getModalByUrl();
			if (popupModal) {
				openModalByUrl();
				setSelectedCruise(null);
			} else {
				if (selectedCruise && !filterOpen) {
					openCruise(
						selectedCruise,
						cruises?.findIndex((cruise) => cruise.id === selectedCruise?.id),
					);
					setSelectedCruise(null);
				}
			}
		}
	}, [selectedCruise, finishedAnimation, cruises, openCruise]);

	useEffect(() => {
		if (comparisonViewState === CLOSED && cruisesComparing.length === 1) {
			setComparisonViewSelecting();
		}

		if (comparisonViewState === SELECTING && cruisesComparing.length === 0) {
			setComparisonViewClosed();
		}
	}, [cruisesComparing]);

	useEffect(() => {
		if ('IntersectionObserver' in window) {
			const elementRef = document.getElementById(PAGE_HEADER_ID);
			const observer = new IntersectionObserver(
				([e]) => {
					setHeaderIsSticky(e.intersectionRatio <= 0);
				},
				{
					threshold: [0],
				},
			);

			if (elementRef) {
				observer.observe(elementRef);
			}

			return function () {
				observer.unobserve(elementRef);
			};
		}
	}, []);

	const showBanner = () => {
		const arrCruise = Array.isArray(cruises) ? cruises : [];
		return arrCruise.length > 0 && arrCruise.length <= 2;
	};

	const fetchMoreResults = () => {
		fetchMoreSearchResults(utagPagination);
	};

	const handleCruiseCardClick = (cruise) => {
		selectGroupIdDetail(cruise.id);
	};

	const renderCruiseCard = (cruise, index, selected) => {
		const defaultCruiseCardVariant = CruiseCardVariant.Refined;

		const bannerPosition = Number(index) === 2;

		return (
			<Fragment key={`search-result-${cruise.id}-${index}`}>
				<CruiseCard
					cruise={cruise.cruise}
					listItemIndex={index}
					variant={defaultCruiseCardVariant}
					listCount={count}
					onClick={() => handleCruiseCardClick(cruise.cruise.cruiseSrc)}
				/>
				{bannerPosition && getBannerToShow()}
			</Fragment>
		);
	};

	return (
		<Constrain ref={wrapperRef} className={selectingForCompare && 'compare'}>
			<HeaderConstrain ref={headerRef} className={[selectingForCompare && 'compare', headerIsSticky && 'isSticky']}>
				<ResultsHeader passedRefs={passedRefs} cruises={cruises} />
			</HeaderConstrain>
			<ResultsWrapper ref={passedRefs?.current?.searchCardsRef} id="cruise-results-wrapper">
				{cruises.map((cruise, index) => {
					const selected = !!cruisesComparing.find((c) => c.id === cruise.id);

					return renderCruiseCard(cruise, index, selected);
				})}
				{showBanner() && getBannerToShow()}
				{loading && <ResultsLoading />}
			</ResultsWrapper>
			{more && !loading && (
				<ButtonContainer id={LOAD_MORE_BUTTON_ID}>
					<Button
						data-testid="load-more-button"
						onClick={fetchMoreResults}
						color={uiSettings.loadMoreButton.color}
						variant={uiSettings.loadMoreButton.variant}
						uppercase={uiSettings.loadMoreButton.uppercase}
						size={uiSettings.loadMoreButton.size}
						fullWidth
					>
						{actionLoadMore}
					</Button>
				</ButtonContainer>
			)}
			{results?.noResults && <NoResults />}
			{!!enableMedalliaEmbeddedSurvey && <EmbeddedSurvey surveyId={enableMedalliaEmbeddedSurvey} isMoreButtonPresent={more} />}
		</Constrain>
	);
}

Results.propTypes = {
	passedRefs: PropTypes.object,
	scrollingElement: PropTypes.object.isRequired,
	forceResize: PropTypes.func.isRequired,
	router: PropTypes.shape({
		query: PropTypes.shape({
			itineraryPanel: PropTypes.string,
			search: PropTypes.string,
			qualifiers: PropTypes.string,
		}).isRequired,
	}),
};

export default withRouter(withForceUpdateResize(withScrollingElement(Results, DEFAULT), DEFAULT));
